import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Helmet } from 'react-helmet-async';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import Spacing from '../Spacing';
import SectionHeadingStyle3 from '../SectionHeading/SectionHeadingStyle3';
import SEO from '../SEO';
import { pageTitle } from '../../helpers/PageTitle';
import { useParams, useNavigate } from "react-router-dom";
import RichText from '../RichText';
import { useCategories } from '../Providers/CategoryContext';
import likeService from '../../services/likeService';
import authService from '../../services/authService';
import commentService from '../../services/commentService';
import CommentForm from '../Comments/CommentForm';
import CommentsList from '../Comments/CommentsList';
import axios from 'axios';
import './blogDetails.scss';

export default function BlogDetailsPage() {
  const { blogId }= useParams();
  const [blog, setBlog] = useState(null)
  const { categories } = useCategories();
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [likeId, setLikeId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const navigate = useNavigate();

  pageTitle('Blog Details');

  function formatDate(dateStr) {
    if (!dateStr) return 'No date';
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  const mapCategory = (categoryData) => {
    if (categoryData && typeof categoryData === 'object' && categoryData.name) {
      return categoryData.name;
    }
    if (categoryData && typeof categoryData === 'number') {
      const result = categories.find(item => item.id === categoryData);
      return result ? result.name : 'Uncategorized';
    }
    return 'Uncategorized';
  }

    
  const calculateReadTime = (content) => {
    if (!content) return 1;
    const wordsPerMinute = 200;
    const textContent = content.replace(/<[^>]*>/g, '');
    const wordCount = textContent.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime || 1;
  };

  const getAuthorInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getExcerpt = (html, maxLength = 155) => {
    if (!html) return '';
    const text = html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getStructuredData = () => {
    if (!blog) return null;

    const authorName = blog.user_created?.first_name 
      ? `${blog.user_created.first_name} ${blog.user_created.last_name || ''}`
      : 'DigitalByte Team';

    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "image": blog.hero_image ? `${import.meta.env.REACT_APP_CMS_URL}/assets/${blog.hero_image}` : null,
      "datePublished": blog.date_created,
      "dateModified": blog.date_updated || blog.date_created,
      "author": {
        "@type": "Person",
        "name": authorName
      },
      "publisher": {
        "@type": "Organization",
        "name": "DigitalByte LLC",
        "logo": {
          "@type": "ImageObject",
          "url": "https://digitalbyte.io/favicon512.png"
        }
      },
      "description": getExcerpt(blog.body),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://digitalbyte.io/blog/${blog.id}`
      }
    };
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (authService.isAuthenticated()) {
        const user = await authService.getCurrentUser();
        setCurrentUser(user);
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await fetch(`${import.meta.env.REACT_APP_CMS_URL}/items/posts/${blogId}?fields=*,user_created.id,user_created.first_name,user_created.last_name,user_created.avatar`);
        if (!res.ok) throw new Error('Failed to load posts');
        const result = await res.json()
        setBlog(result.data)
      } catch (error) {
        console.error('Error fetching blog:', error);
        setBlog(null);
      }
    }
    fetchBlog()
  },[blogId])

  useEffect(() => {
    const fetchLikeData = async () => {
      if (!blogId) return;

      try {
        // Get like count
        const likeCountRes = await axios.get(
          `${import.meta.env.REACT_APP_CMS_URL}/items/post_likes?filter[post][_eq]=${blogId}&aggregate[count]=id`
        );
        const count = likeCountRes.data.data?.[0]?.count?.id || 0;
        setLikeCount(count);

        // Check if current user has liked this post
        if (currentUser && currentUser.id) {
          const token = authService.getAccessToken();
          const userLikeRes = await axios.get(
            `${import.meta.env.REACT_APP_CMS_URL}/items/post_likes?filter[post][_eq]=${blogId}&filter[user][_eq]=${currentUser.id}&limit=1`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const userLikes = userLikeRes.data.data;
          if (userLikes && userLikes.length > 0) {
            setIsLiked(true);
            setLikeId(userLikes[0].id);
          } else {
            setIsLiked(false);
            setLikeId(null);
          }
        }
      } catch (error) {
        console.error('Error fetching like data:', error);
      }
    };

    fetchLikeData();
  }, [blogId, currentUser]);

  useEffect(() => {
    const fetchComments = async () => {
      if (!blogId) return;

      try {
        const commentsData = await commentService.getCommentsByPost(blogId);
        setComments(commentsData);
        setCommentCount(commentsData.length);
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };

    fetchComments();
  }, [blogId]);

  const handleLikeClick = async () => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Prevent multiple clicks
    if (isLoading) return;

    setIsLoading(true);

    // Get user outside try block so it's available in catch block
    const user = authService.getUser();
    if (!user) {
      navigate('/login');
      setIsLoading(false);
      return;
    }

    try {
      if (isLiked) {
        // Unlike the post
        await likeService.unlikePost(likeId);
        setIsLiked(false);
        setLikeId(null);
        setLikeCount(prev => Math.max(0, prev - 1));
      } else {
        // Like the post
        const result = await likeService.likePost(parseInt(blogId), user.id);
        setIsLiked(true);
        setLikeId(result.likeId);
        setLikeCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);

      // If it's a duplicate error, refresh the like status
      if (error.message && error.message.includes('already liked')) {
        // Refresh the like data to get the correct state
        const token = authService.getAccessToken();
        try {
          const userLikeRes = await axios.get(
            `${import.meta.env.REACT_APP_CMS_URL}/items/post_likes?filter[post][_eq]=${blogId}&filter[user][_eq]=${user.id}&limit=1`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const userLikes = userLikeRes.data.data;
          if (userLikes && userLikes.length > 0) {
            setIsLiked(true);
            setLikeId(userLikes[0].id);
          }
        } catch (refreshError) {
          console.error('Error refreshing like status:', refreshError);
        }
      }

      alert(error.message || 'Failed to update like status');
    } finally {
      setIsLoading(false);
    }
  };

  const formatLikeCount = (count) => {
    if (count === 0) return 'Be the first to like this';
    if (count === 1) return isLiked ? 'You like this' : '1 person likes this';
    if (isLiked) {
      return count === 2 ? 'You and 1 other like this' : `You and ${count - 1} others like this`;
    }
    return `${count} people like this`;
  };

  const handleCommentAdded = (newComment) => {
    // Fetch updated user details for the new comment
    const fetchCommentWithUser = async () => {
      try {
        const commentsData = await commentService.getCommentsByPost(blogId);
        setComments(commentsData);
        setCommentCount(commentsData.length);
      } catch (error) {
        console.error('Error refreshing comments:', error);
      }
    };
    fetchCommentWithUser();
  };

  const handleCommentEdit = (updatedComment) => {
    setComments(comments.map(comment =>
      comment.id === updatedComment.id ? { ...comment, ...updatedComment } : comment
    ));
  };

  const handleCommentDelete = (commentId) => {
    setComments(comments.filter(comment => comment.id !== commentId));
    setCommentCount(prev => Math.max(0, prev - 1));
  };

  return (
    <>
      {blog && (
        <>
          <SEO
            title={blog.title}
            description={getExcerpt(blog.body)}
            keywords={blog.tags ? blog.tags.join(', ') : ''}
            image={blog.hero_image ? `${import.meta.env.REACT_APP_CMS_URL}/assets/${blog.hero_image}` : null}
            url={`/blog/${blog.id}`}
            type="article"
            author={blog.user_created?.first_name 
              ? `${blog.user_created.first_name} ${blog.user_created.last_name || ''}`
              : 'DigitalByte Team'}
            publishedTime={blog.date_created}
            modifiedTime={blog.date_updated}
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify(getStructuredData())}
            </script>
          </Helmet>
        </>
      )}
      <div>
        { blog ? (
          <div>
            <Spacing lg="70" md="70" />
            <Spacing lg="140" md="80" />
            <SectionHeadingStyle3
              title={blog.title}
              variant="text-center"
              shape="shape_5"
              category={mapCategory(blog.category)}
              date={formatDate(blog.date_created)}
              avatar="Tech"
              avatarLink="/"
            />
            <Spacing lg="75" md="60" />
            
            <div className="cs_blog_detail_hero">
              {blog.hero_image && (
                <img
                  src={`${import.meta.env.REACT_APP_CMS_URL}/assets/${blog.hero_image}`}
                  alt={blog.title}
                />
              )}
            </div>

            <Spacing lg="60" md="40" />

            <div className="container">
              <div className="cs_blog_content_wrapper">
                <div className="cs_post_details">
                  <RichText html={blog.body} />
                </div>
                
                <div className="cs_height_30 cs_height_lg_10" />

                <div className="cs_blog_like_section">
                  <div className="cs_blog_like_container">
                    <button
                      className={`cs_blog_like_btn ${isLiked ? 'liked' : ''}`}
                      onClick={handleLikeClick}
                      disabled={isLoading}
                      title={isLiked ? 'Unlike this post' : 'Like this post'}
                    >
                      {isLiked ? <FaHeart /> : <FaRegHeart />}
                      <span>{formatLikeCount(likeCount)}</span>
                    </button>
                  </div>
                </div>

                <div className="cs_height_30 cs_height_lg_10" />

                <div className="cs_post_share">
                  <div className="cs_categories">
                    {blog.tags ? blog.tags.map((item, index) => (
                      <a href="/" key={index} className="cs_category">
                        {item}
                      </a>
                    )) : <></>}
                  </div>
                </div>

                <div className="cs_height_50 cs_height_lg_40" />

                <div className="cs_comments_section">
                  <div className="cs_comments_header">
                    <h3>{commentCount} Comment{commentCount !== 1 ? 's' : ''}</h3>
                  </div>

                  <div className="cs_height_30 cs_height_lg_20" />

                  <CommentForm
                    postId={blogId}
                    onCommentAdded={handleCommentAdded}
                  />

                  <div className="cs_height_40 cs_height_lg_30" />

                  <CommentsList
                    comments={comments}
                    currentUserId={currentUser?.id}
                    onEdit={handleCommentEdit}
                    onDelete={handleCommentDelete}
                  />
                </div>

                <div className="cs_height_50 cs_height_lg_40" />

                <div className="cs_blog_author_info">
                  <div className="cs_blog_author_avatar">
                    {blog.user_created?.avatar ? (
                      <img
                        src={`${import.meta.env.REACT_APP_CMS_URL}/assets/${blog.user_created.avatar}`}
                        alt={`${blog.user_created.first_name} ${blog.user_created.last_name}`}
                      />
                    ) : (
                      <div className="cs_blog_author_placeholder">
                        {getAuthorInitials(
                          blog.user_created?.first_name 
                            ? `${blog.user_created.first_name} ${blog.user_created.last_name || ''}`
                            : 'Unknown'
                        )}
                      </div>
                    )}
                  </div>
                  <div className="cs_blog_author_details">
                    <h3 className="cs_blog_author_name">
                      {blog.user_created?.first_name 
                        ? `${blog.user_created.first_name} ${blog.user_created.last_name || ''}`
                        : 'Unknown Author'}
                    </h3>
                    <div className="cs_blog_author_meta">
                      <div className="cs_blog_author_date">
                        <Icon icon="material-symbols:calendar-today" />
                        <span>{formatDate(blog.date_created)}</span>
                      </div>
                      <div className="cs_blog_author_read_time">
                        <Icon icon="material-symbols:schedule" />
                        <span>{calculateReadTime(blog.body)} min read</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ): <p>Loading</p>}
      </div>
    </>
  );
}
